import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Header, Footer } from "@/components/SiteChrome";
import { ProductGallery } from "@/components/ProductGallery";
import { ProductTile } from "@/components/ProductTile";
import { RecentlyViewed } from "@/components/RecentlyViewed";
import { cart } from "@/lib/cart";
import { recentlyViewed } from "@/lib/recentlyViewed";
import { useAuth } from "@/lib/auth";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import api from "@/lib/api";
import { Heart } from "lucide-react";

export default function ProductDetail() {
    const { slug } = useParams();
    const [p, setP] = useState(null);
    const [related, setRelated] = useState([]);
    const [qty, setQty] = useState(1);
    const [added, setAdded] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const [inWishlist, setInWishlist] = useState(false);
    const { user } = useAuth();

    useEffect(() => {
        setNotFound(false);
        api.get(`/products/${slug}`).then(({ data }) => {
            setP(data);
            recentlyViewed.push(data);
            api.get("/products", { params: { category: data.category, limit: 8 } }).then(({ data: r }) => {
                setRelated(r.items.filter((x) => x.slug !== slug).slice(0, 3));
            });
        }).catch(() => setNotFound(true));
    }, [slug]);

    useDocumentMeta({
        title: p ? `${p.name} · Urban Off-Road` : "Urban Off-Road",
        description: p ? (p.blurb || `${p.name} — ${p.category} for ${p.fitment}. $${Number(p.price || 0).toFixed(2)}.`).replace(/<[^>]+>/g, "").slice(0, 240) : undefined,
        image: p?.images?.[0],
        url: typeof window !== "undefined" ? window.location.href : undefined,
        type: "product",
    });

    useEffect(() => {
        if (user) {
            api.get("/wishlist").then(({ data }) => {
                setInWishlist(!!data.items.find((x) => x.slug === slug));
            }).catch(() => {});
        }
    }, [user, slug]);

    const toggleWishlist = async () => {
        if (!user) {
            toast.error("Sign in to save to your wishlist");
            return;
        }
        try {
            const { data } = await api.post("/wishlist/toggle", { slug });
            setInWishlist(data.in_wishlist);
            toast.success(data.in_wishlist ? "Added to wishlist" : "Removed from wishlist");
        } catch { toast.error("Something went wrong"); }
    };

    if (notFound) {
        return (
            <div className="min-h-screen bg-[hsl(var(--dust-50))]">
                <Header />
                <div className="mx-auto max-w-3xl px-4 py-32 text-center">
                    <div className="font-display text-6xl uppercase italic text-[hsl(var(--ink))]">Part not found.</div>
                    <Link to="/shop" className="mt-6 inline-block font-heading text-sm uppercase underline underline-offset-8 text-[hsl(var(--ink))]">
                        Back to shop
                    </Link>
                </div>
                <Footer />
            </div>
        );
    }

    if (!p) {
        return (
            <div className="min-h-screen bg-[hsl(var(--dust-50))]">
                <Header />
                <div className="mx-auto max-w-7xl px-4 py-12">
                    <div className="animate-pulse">
                        <div className="aspect-square max-w-lg bg-[hsl(var(--dust-100))]" />
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    const add = () => {
        cart.add(p.slug, qty);
        setAdded(true);
        toast.success(`${p.name} added to your build`);
        setTimeout(() => setAdded(false), 1800);
    };

    return (
        <div className="min-h-screen bg-[hsl(var(--dust-50))]" data-testid="pdp-page">
            <Header />

            <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
                <nav className="font-mono-brand text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                    <Link to="/" className="hover:text-[hsl(var(--ink))]">Home</Link> /{" "}
                    <Link to="/shop" className="hover:text-[hsl(var(--ink))]">Shop</Link> /{" "}
                    <span>{p.category}</span> / <span className="text-[hsl(var(--ink))]">{p.name}</span>
                </nav>
            </div>

            <div className="mx-auto grid max-w-7xl gap-12 px-4 pb-20 sm:px-6 lg:grid-cols-2">
                <ProductGallery p={p} />

                <div>
                    <div className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[hsl(var(--ink-soft))]">
                        {p.category} · {p.sku}
                    </div>
                    <h1 className="mt-3 font-display text-5xl uppercase italic leading-none md:text-6xl text-[hsl(var(--ink))]">{p.name}</h1>
                    <div className="mt-4 flex items-center gap-4">
                        <div className="font-display text-4xl text-[hsl(var(--ink))]" data-testid="pdp-price">${p.price}</div>
                        <div className={`font-mono-brand text-xs uppercase tracking-wider ${p.stock > 0 ? "text-[hsl(var(--success))]" : "text-[hsl(var(--danger))]"}`}>
                            {p.stock > 0 ? `● In stock · ${p.stock} ready` : "● Sold out"}
                        </div>
                    </div>
                    <p className="mt-6 text-lg text-[hsl(var(--ink-soft))]">{p.blurb}</p>

                    <div className="mt-6 border border-[hsl(var(--ink))] bg-[hsl(var(--dust-300))] p-4 text-[hsl(var(--dust-50))]">
                        <div className="font-mono-brand text-[10px] uppercase tracking-wider text-[hsl(var(--dust-100))]">Fitment</div>
                        <div className="mt-1 font-heading text-base uppercase">{p.fitment}</div>
                    </div>

                    {p.specs && p.specs.length > 0 && (
                        <div className="mt-6">
                            <div className="font-mono-brand text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">Specs</div>
                            <dl className="mt-2 divide-y divide-[hsl(var(--dust-300))] border-y border-[hsl(var(--dust-300))]">
                                {p.specs.map((s) => (
                                    <div key={s.label} className="flex justify-between py-3 text-sm">
                                        <dt className="font-heading uppercase tracking-wider">{s.label}</dt>
                                        <dd className="font-mono-brand">{s.value}</dd>
                                    </div>
                                ))}
                            </dl>
                        </div>
                    )}

                    <div className="mt-8 flex flex-wrap items-center gap-4">
                        <div className="flex items-stretch border-2 border-[hsl(var(--ink))]" data-testid="pdp-qty">
                            <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 font-display text-2xl text-[hsl(var(--ink))]" data-testid="pdp-qty-minus">−</button>
                            <div className="flex w-12 items-center justify-center font-mono-brand text-[hsl(var(--ink))]">{qty}</div>
                            <button onClick={() => setQty(qty + 1)} className="px-4 font-display text-2xl text-[hsl(var(--ink))]" data-testid="pdp-qty-plus">+</button>
                        </div>
                        <button
                            onClick={add}
                            disabled={p.stock === 0}
                            className="btn-primary inline-flex items-center gap-3 disabled:cursor-not-allowed disabled:bg-[hsl(var(--dust-300))] disabled:opacity-60"
                            data-testid="pdp-add-cart"
                        >
                            {added ? "Added to build ✓" : "Add to build →"}
                        </button>
                        <button
                            onClick={toggleWishlist}
                            className={`border-2 border-[hsl(var(--ink))] px-4 py-3 font-heading text-xs uppercase tracking-wider transition-colors ${inWishlist ? "bg-[hsl(var(--rust))] text-[hsl(var(--dust-50))]" : "bg-transparent text-[hsl(var(--ink))] hover:bg-[hsl(var(--ink))] hover:text-[hsl(var(--dust-50))]"}`}
                            data-testid="pdp-wishlist"
                            aria-label="Toggle wishlist"
                        >
                            <Heart className={`h-4 w-4 ${inWishlist ? "fill-current" : ""}`} />
                        </button>
                    </div>
                </div>
            </div>

            {p.description && (
                <section className="border-t border-[hsl(var(--dust-300))]">
                    <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6">
                        <div className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[hsl(var(--ink-soft))]">Description</div>
                        <div
                            className="prose prose-sm mt-4 max-w-none font-sans text-[hsl(var(--ink))] [&_a]:text-[hsl(var(--forest))] [&_a]:underline [&_h2]:font-heading [&_h2]:text-xl [&_h2]:uppercase [&_h3]:font-heading [&_h3]:uppercase [&_li]:my-1 [&_p]:my-3 [&_ul]:list-disc [&_ul]:pl-6"
                            dangerouslySetInnerHTML={{ __html: p.description }}
                        />
                    </div>
                </section>
            )}

            {related.length > 0 && (
                <section className="border-t-2 border-[hsl(var(--ink))] bg-[hsl(var(--dust-300))] text-[hsl(var(--dust-50))]">
                    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
                        <h2 className="font-display text-3xl uppercase italic md:text-4xl">More in {p.category}.</h2>
                        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {related.map((r) => <ProductTile key={r.slug} p={r} />)}
                        </div>
                    </div>
                </section>
            )}

            <RecentlyViewed excludeSlug={slug} />

            <Footer />
        </div>
    );
}
